import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Badge,
  Dropdown,
  MessageBar,
  MessageBarBody,
  Input,
  Option,
  Spinner,
  Tooltip,
  Text,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  MAX_PTO_HOURS_PER_DAY,
  assessPtoStanding,
  ptoStatusLabel,
  validatePtoEntry,
  type PtoEntry,
  type PtoStanding,
  type PtoStandingAssessment,
  type PtoEntryInput,
  type PtoIssue,
  type PtoOptions,
  type PtoStatus,
} from '@worklogix/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { MOBILE, SHRINKABLE } from '../../responsive';
import { useTimeAdminApi } from '../api';
import { toErrorMessage } from '../errors';
import { useIsAdmin } from '../useIsAdmin';

import { groupPtoByEmployee, type PtoBucket } from './ptoGrouping';
import { useColumnFilters, type FilterableColumn } from './useColumnFilters';

/** Local calendar date as ISO `YYYY-MM-DD` (en-CA formats exactly that way). */
function todayIso(): string {
  return new Date().toLocaleDateString('en-CA');
}

/** A row being created or edited. `id` is present only when editing. */
interface Draft {
  mode: 'new' | 'edit';
  id?: string;
  form: PtoEntryInput;
}

const EMPTY_FORM: PtoEntryInput = {
  employeeId: '',
  departmentId: '',
  startDate: '',
  endDate: '',
  hours: 0,
  note: null,
};

/**
 * Data columns, left to right, plus the Flag column appended separately — flag
 * text is transient validation output, so there is nothing to filter it on.
 */
const DATA_COLUMN_COUNT = 5;
const TOTAL_COLUMNS = DATA_COLUMN_COUNT + 1;

const useStyles = makeStyles({
  root: {
    display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalM,
    ...SHRINKABLE,
  },
  toolbar: {
    // A row of buttons plus a filter dropdown cannot fit 390px. Wrapping keeps every
    // control reachable instead of pushing the last ones off-screen.
    [MOBILE]: {
      flexWrap: 'wrap',
      rowGap: tokens.spacingVerticalS,
    },
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  spacer: { flexGrow: 1 },
  employeeFilter: {
    minWidth: '220px',
    // A 220px control plus buttons cannot share a 390px row; full width
    // reads as a field rather than a cramped stub.
    [MOBILE]: { minWidth: 0, width: '100%' },
  },

  // ONLY the table scrolls — the toolbar and the report picker stay put while a
  // long list is reviewed.
  scroller: {
    // A data table stays TABULAR on a phone and scrolls sideways INSIDE this box —
    // columns like date/hours/status are only meaningful next to each other. The
    // SHRINKABLE part is what makes that work: without minWidth:0 this box refuses to
    // become narrower than the table and the page scrolls instead.
    ...SHRINKABLE,
    WebkitOverflowScrolling: 'touch',
    maxHeight: '62vh',
    overflowY: 'auto',
    overflowX: 'auto',
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  // A bespoke native table rather than Fluent's <Table>: only a real <table>
  // supports the colSpan employee-block headers, and its cells size to their
  // content instead of clipping it — which is what truncated flag messages.
  // `separate` + zero spacing keeps position:sticky working on the head cells;
  // `collapse` breaks sticky in several browsers.
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    fontSize: tokens.fontSizeBase300,
    tableLayout: 'auto',
  },
  headCell: {
    [MOBILE]: {
      paddingLeft: tokens.spacingHorizontalXS,
      paddingRight: tokens.spacingHorizontalXS,
      fontSize: tokens.fontSizeBase200,
    },
    textAlign: 'left',
    verticalAlign: 'bottom',
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground1,
    whiteSpace: 'nowrap',
    position: 'sticky',
    top: 0,
    zIndex: 3,
  },
  /**
   * The per-column filter strip is a whole extra row of dropdowns. Useful with a
   * mouse and a wide table; on a phone it is a screenful of controls above the
   * data you came to read (Michael, 2026-09-10). Hidden below the breakpoint —
   * the report-level pickers above the table still narrow the set.
   */
  filterRow: {
    [MOBILE]: { display: 'none' },
  },
  filterCell: {
    paddingTop: 0,
    paddingBottom: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
    position: 'sticky',
    top: '30px',
    zIndex: 2,
  },
  filterDropdown: {
    minWidth: '120px', width: '100%',
    // A 220px control plus buttons cannot share a 390px row; full width
    // reads as a field rather than a cramped stub.
    [MOBILE]: { minWidth: 0, width: '100%' },
  },

  // Rows grow to fit their content: every cell wraps and aligns to the top, so
  // nothing is clipped however long a flag message runs.
  cell: {
    [MOBILE]: {
      paddingLeft: tokens.spacingHorizontalXS,
      paddingRight: tokens.spacingHorizontalXS,
      fontSize: tokens.fontSizeBase200,
    },
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke3,
    verticalAlign: 'top',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    lineHeight: tokens.lineHeightBase300,
  },
  // Dates and hours are short and read better unbroken.
  cellNoWrap: { whiteSpace: 'nowrap' },
  hoursCell: { whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
  // A reduced booking shows what is PAYABLE, with the original underneath — the
  // request is never overwritten, so both numbers stay visible.
  hoursReduced: { fontWeight: tokens.fontWeightSemibold, color: tokens.colorStatusWarningForeground1 },
  hoursOriginal: {
    display: 'block',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    textDecorationLine: 'line-through',
  },
  // The flag gets real room and always wraps. This is the cell that used to be
  // truncated, and it is the only place a rejected submit explains itself.
  flagCell: {
    minWidth: '260px',
    maxWidth: '420px',
    color: tokens.colorPaletteRedForeground1,
  },
  dataRow: {
    cursor: 'pointer',
    '&:hover td': { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  selectedRow: {
    '& td': { backgroundColor: tokens.colorNeutralBackground1Selected },
    '&:hover td': { backgroundColor: tokens.colorNeutralBackground1Selected },
  },
  draftRow: {
    '& td': { backgroundColor: tokens.colorNeutralBackground3 },
  },

  // --- Employee block header ("at a glance"), matching the timecard surface ---
  glanceCell: {
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground2,
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke3,
  },
  glanceHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  glanceName: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorNeutralForeground1,
  },
  glanceTotalsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    columnGap: tokens.spacingHorizontalL,
    rowGap: tokens.spacingVerticalXXS,
    alignItems: 'baseline',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  glanceTotalGroup: {
    display: 'inline-flex',
    alignItems: 'baseline',
    gap: tokens.spacingHorizontalXS,
    paddingLeft: tokens.spacingHorizontalL,
    borderLeftWidth: '1px',
    borderLeftStyle: 'solid',
    borderLeftColor: tokens.colorNeutralStroke2,
  },
  glanceTotalLabel: { fontWeight: tokens.fontWeightBold, color: tokens.colorNeutralForeground1 },
  /**
   * PTO balances live in the block header beside the name (Michael, 2026-09-13),
   * always on, split per department. Chips rather than one sentence: an employee
   * can hold nine accruals, and nine facts in a run-on line is the eyesore this
   * replaced.
   */
  balanceRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXS,
    alignItems: 'center',
    marginTop: tokens.spacingVerticalXXS,
  },
  balanceChip: {
    display: 'inline-flex',
    alignItems: 'baseline',
    gap: tokens.spacingHorizontalXXS,
    paddingLeft: tokens.spacingHorizontalSNudge,
    paddingRight: tokens.spacingHorizontalSNudge,
    paddingTop: '1px',
    paddingBottom: '1px',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    fontSize: tokens.fontSizeBase200,
    whiteSpace: 'nowrap',
  },
  balanceDept: { fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1 },
  balanceLeft: { fontVariantNumeric: 'tabular-nums' },
  balanceBooked: { color: tokens.colorStatusWarningForeground1, fontVariantNumeric: 'tabular-nums' },
  balanceOver: { color: tokens.colorPaletteRedForeground1, fontWeight: tokens.fontWeightSemibold },
  balanceNote: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  // Upcoming is the number this screen exists to surface — time booked but not
  // yet taken, and whether someone already has several queued up.
  upcoming: { fontWeight: tokens.fontWeightSemibold, color: tokens.colorStatusWarningForeground1 },
  active: { fontWeight: tokens.fontWeightSemibold, color: tokens.colorBrandForeground1 },
  complete: { color: tokens.colorNeutralForeground3 },
  glanceSep: { color: tokens.colorNeutralForeground4 },
  newBlockLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },

  empty: {
    padding: tokens.spacingVerticalXXL,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
  error: { color: tokens.colorPaletteRedForeground1 },
  numeric: { maxWidth: '110px' },
  dateInput: {
    fontFamily: tokens.fontFamilyBase,
    fontSize: tokens.fontSizeBase300,
    padding: '4px 6px',
    borderRadius: tokens.borderRadiusMedium,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
  },
  hint: { color: tokens.colorNeutralForeground3 },
});

type Styles = ReturnType<typeof useStyles>;

/** `3 · 24h`-style fragment for one status bucket. */
function bucketText(bucket: PtoBucket): string {
  return `${String(bucket.count)} · ${String(bucket.hours)}h`;
}

/**
 * The PTO report table.
 *
 * Grouped into per-employee blocks (the same shape as the timecard surface) so a
 * supervisor can see at a glance whether someone already has several future
 * bookings. Because the block header carries the name there is no Employee
 * column — filtering by person is the toolbar dropdown instead.
 *
 * VISIBILITY: an Admin sees every entry; a supervisor sees entries for their
 * NetSuite reports plus any entry they created themselves. That is enforced by
 * the API — this component renders whatever it is given and decides nothing.
 *
 * Status is derived from the dates by the API (Pending before the range, Active
 * during, Complete after), so rows advance on their own; the client only shows
 * "New" for a row that has not been saved yet.
 */

export function PtoReportTable(): JSX.Element {
  const styles = useStyles();
  const api = useTimeAdminApi();
  const isAdmin = useIsAdmin();

  const [entries, setEntries] = useState<readonly PtoEntry[] | null>(null);
  const [options, setOptions] = useState<PtoOptions | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  /**
   * The four PTO figures per EMPLOYEE, keyed by WorkLogix employee id.
   *
   * Fetched for every distinct employee on the page, not just the selected one, because
   * Michael wants the numbers ON the entries — "This is the data that I need populating
   * on the entries in the PTO report". Affordable now that a standing is two cheap reads
   * (~2.5s, cached 5 minutes server-side); it would not have been under the old
   * paycheck-scanning balance, which is why that was deleted.
   */
  const [standings, setStandings] = useState<Map<string, PtoStanding>>(new Map());
  const [approving, setApproving] = useState(false);
  const [approveResult, setApproveResult] = useState<string | null>(null);
  const [issues, setIssues] = useState<readonly PtoIssue[]>([]);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  /**
   * '' = every employee. Lives outside useColumnFilters deliberately: it is a
   * toolbar control, because the Employee column no longer exists to hang a
   * per-column filter on.
   */
  const [employeeFilter, setEmployeeFilter] = useState('');

  const reload = useCallback(async (): Promise<void> => {
    const [list, opts] = await Promise.all([api.listPtoEntries(), api.getPtoOptions()]);
    setEntries(list);
    setOptions(opts);
  }, [api]);

  // One standing per DISTINCT employee on the page. Keyed on the set of employee ids, so
  // editing a booking's dates or hours does not refetch anything.
  const employeeIdsKey = useMemo(
    () => [...new Set((entries ?? []).map((e) => e.employeeId))].sort().join(','),
    [entries]
  );
  useEffect(() => {
    if (employeeIdsKey === '' || options === null) {
      return;
    }
    let active = true;
    const byId = new Map(options.employees.map((e) => [e.id, e.nsEmployeeId]));
    void Promise.all(
      employeeIdsKey.split(',').map(async (employeeId) => {
        const nsId = byId.get(employeeId) ?? null;
        if (nsId === null || nsId === '') {
          // Not linked to NetSuite, so there is nothing to read. Said out loud rather
          // than left blank: "cannot look this up" is a different fact from "none left".
          return [
            employeeId,
            {
              allottedHours: null,
              usedHours: null,
              futureBookedHours: 0,
              availableIfFutureAppliedHours: null,
              availableNowHours: null,
              buckets: [],
              unavailable: 'not linked to a NetSuite employee record',
            } satisfies PtoStanding,
          ] as const;
        }
        try {
          return [employeeId, await api.getPtoStanding(nsId)] as const;
        } catch {
          return [
            employeeId,
            {
              allottedHours: null,
              usedHours: null,
              futureBookedHours: 0,
              availableIfFutureAppliedHours: null,
              availableNowHours: null,
              buckets: [],
              unavailable: 'the balance could not be read from NetSuite',
            } satisfies PtoStanding,
          ] as const;
        }
      })
    ).then((pairs) => {
      if (active) {
        setStandings(new Map(pairs));
      }
    });
    return () => {
      active = false;
    };
  }, [api, employeeIdsKey, options]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [list, opts] = await Promise.all([api.listPtoEntries(), api.getPtoOptions()]);
        if (!cancelled) {
          setEntries(list);
          setOptions(opts);
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(toErrorMessage(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api]);

  /** People who actually have entries — the toolbar filter offers only these. */
  const employeeChoices = useMemo(() => {
    const seen = new Map<string, string>();
    for (const entry of entries ?? []) {
      seen.set(entry.employeeId, entry.employeeName);
    }
    return [...seen.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [entries]);

  // The employee filter narrows the set BEFORE the column filters, so each
  // column dropdown offers only values present for the chosen person.
  const scoped = useMemo(() => {
    const all = entries ?? [];
    return employeeFilter === '' ? all : all.filter((e) => e.employeeId === employeeFilter);
  }, [entries, employeeFilter]);

  const columns: ReadonlyArray<FilterableColumn<PtoEntry>> = useMemo(
    () => [
      { columnId: 'department', header: 'Department', filterValue: (r) => r.departmentName },
      { columnId: 'startDate', header: 'Start date', filterValue: (r) => r.startDate },
      { columnId: 'endDate', header: 'End date', filterValue: (r) => r.endDate },
      { columnId: 'hours', header: 'Hours', filterValue: (r) => String(r.hours) },
    ],
    []
  );

  const {
    selections,
    options: filterOptions,
    setSelection,
    clearAll,
    filtered,
    activeCount,
  } = useColumnFilters(scoped, columns);

  const blocks = useMemo(() => groupPtoByEmployee(filtered), [filtered]);

  /** Validation context built from what is currently loaded. */
  const validate = useCallback(
    (form: PtoEntryInput, editingId: string | undefined): PtoIssue[] => {
      if (options === null || entries === null) {
        return [];
      }
      return validatePtoEntry(form, {
        employeeIds: new Set(options.employees.map((e) => e.id)),
        departmentIds: new Set(options.departments.map((d) => d.id)),
        existing: entries.map((e) => ({
          id: e.id,
          employeeId: e.employeeId,
          startDate: e.startDate,
          endDate: e.endDate,
        })),
        today: todayIso(),
        ...(editingId !== undefined ? { editingId } : {}),
      });
    },
    [options, entries]
  );

  const startNew = useCallback(() => {
    setSelectedId(null);
    setIssues([]);
    setActionError(null);
    setDraft({ mode: 'new', form: { ...EMPTY_FORM } });
  }, []);

  const startEdit = useCallback(() => {
    const row = entries?.find((e) => e.id === selectedId);
    if (row === undefined) {
      return;
    }
    setIssues([]);
    setActionError(null);
    setDraft({
      mode: 'edit',
      id: row.id,
      form: {
        employeeId: row.employeeId,
        departmentId: row.departmentId,
        startDate: row.startDate,
        endDate: row.endDate,
        hours: row.hours,
        note: row.note,
      },
    });
  }, [entries, selectedId]);

  const cancelDraft = useCallback(() => {
    setDraft(null);
    setIssues([]);
    setActionError(null);
  }, []);

  /**
   * Submit the draft. Validation runs HERE first so the Flag cell can explain
   * every broken rule at once without a round trip — the API runs the same rules
   * again and is the authority.
   */
  const submit = useCallback(async (): Promise<void> => {
    if (draft === null) {
      return;
    }
    const found = validate(draft.form, draft.id);
    setIssues(found);
    if (found.length > 0) {
      return;
    }
    setSaving(true);
    setActionError(null);
    try {
      if (draft.mode === 'new') {
        await api.createPtoEntry(draft.form);
      } else {
        const { employeeId: _ignored, ...rest } = draft.form;
        await api.updatePtoEntry(draft.id as string, rest);
      }
      await reload();
      setDraft(null);
      setIssues([]);
    } catch (err) {
      setActionError(toErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }, [api, draft, reload, validate]);

  const doDelete = useCallback(async (): Promise<void> => {
    if (selectedId === null) {
      return;
    }
    setSaving(true);
    setActionError(null);
    try {
      await api.deletePtoEntry(selectedId);
      await reload();
      setSelectedId(null);
    } catch (err) {
      setActionError(toErrorMessage(err));
    } finally {
      setSaving(false);
      setConfirmDelete(false);
    }
  }, [api, reload, selectedId]);

  const patchForm = useCallback((patch: Partial<PtoEntryInput>) => {
    setDraft((prev) => (prev === null ? prev : { ...prev, form: { ...prev.form, ...patch } }));
  }, []);

  // 🔥 THESE TWO HOOKS MUST STAY ABOVE THE EARLY RETURNS BELOW.
  //
  // They were added below them, which meant the loading render (entries === null) ran
  // FEWER hooks than the loaded one — React error #310, "rendered more hooks than during
  // the previous render", which killed the whole My Reports tab the moment data arrived.
  // Hooks are positional: every render must call the same ones in the same order, so no
  // hook may sit after a conditional return.
  /**
   * WorkLogix department id -> NetSuite department id, for bucket matching.
   *
   * 🔥 DECLARED BEFORE THE EARLY RETURNS. This sat below them and blew the whole tab
   * up with React #310 ("rendered more hooks than during the previous render"): the
   * loading render returns at `entries === null` and never reaches this hook, so the
   * first loaded render ran one hook more than the render before it.
   */
  const nsDepartmentByWlxId = useMemo(
    () => new Map((options?.departments ?? []).map((d) => [d.id, d.nsDepartmentId] as const)),
    [options]
  );

  /**
   * The flag for ONE row: does this booking exhaust or overrun the employee's PTO?
   *
   * `assessPtoStanding` is the same function the API side uses, so the screen cannot
   * disagree with the server about whether a booking overdraws somebody.
   *
   * `sentToNetsuiteAt !== null` is what stops double counting: a POSTED booking is
   * already inside the employee's "logged for future use" figure, so adding its hours
   * again would light up every posted row the moment it reached NetSuite.
   */
  const flagFor = useCallback(
    (row: PtoEntry): PtoStandingAssessment | null => {
      const standing = standings.get(row.employeeId);
      if (standing === undefined) {
        return null;
      }
      // Against the ROW'S department bucket, never the total — the buckets are
      // per-department accruals and are not spendable together.
      //
      // `saved: true` because every row on this report IS an app.pto_entry row, and the
      // bucket's booked hours are summed from that table — so the row is already in the
      // figure. Saying "posted" here instead subtracted it a second time and flagged
      // every pending booking as nearly exhausting its own balance.
      return assessPtoStanding(standing, Number(row.hours), {
        saved: true,
        posted: row.sentToNetsuiteAt !== null,
        nsDepartmentId: nsDepartmentByWlxId.get(row.departmentId) ?? null,
      });
    },
    [standings, nsDepartmentByWlxId]
  );

  /**
   * Approve the selected booking AND post it to NetSuite (Michael, 2026-09-04).
   *
   * The server does both in one call, so there is no state here where WorkLogix says
   * approved and NetSuite has never heard of it. A PARTIAL post is reported rather than
   * swallowed: those days did not reach NetSuite and the booking is not approved, and
   * pressing Approve again posts only the days still missing.
   */
  const approveSelected = useCallback(async (): Promise<void> => {
    if (selectedId === null) {
      return;
    }
    setApproving(true);
    setApproveResult(null);
    try {
      const result = await api.approvePtoEntry(selectedId);
      const days = Object.keys(result.posted).length;
      // SAY WHY, not just which date. The server has carried a per-day `reason` since
      // 2026-09-14; this message was still printing dates alone, so the screen showed
      // "1 could not be sent (2026-09-15)" and the reason NetSuite gave went nowhere.
      setApproveResult(
        result.failed.length === 0
          ? `Approved and posted ${String(days)} day(s) to NetSuite.`
          : `Posted ${String(days)} day(s); ${String(result.failed.length)} could not be sent — ` +
            `${result.failed.map((f) => `${f.date}: ${f.reason}`).join('; ')}. ` +
            'The booking is NOT approved — press Approve again to post the rest.'
      );
      await reload();
    } catch (err) {
      setApproveResult(toErrorMessage(err));
    } finally {
      setApproving(false);
    }
  }, [api, reload, selectedId]);

  if (loadError !== null) {
    return (
      <div className={styles.empty} role="alert">
        <Text className={styles.error}>{loadError}</Text>
      </div>
    );
  }
  if (entries === null || options === null) {
    return (
      <div className={styles.empty} role="status">
        <Spinner size="tiny" label="Loading PTO…" />
      </div>
    );
  }


  const draftFlag = issues.map((i) => i.message).join(' ');
  const today = todayIso();
  const selectedRow = entries.find((e) => e.id === selectedId) ?? null;
  const filterLabel =
    employeeFilter === ''
      ? 'All employees'
      : (employeeChoices.find((c) => c.id === employeeFilter)?.name ?? 'All employees');


  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <Button appearance="primary" onClick={startNew} disabled={draft !== null || saving}>
          New PTO
        </Button>
        <Button onClick={() => void submit()} disabled={draft === null || saving}>
          {saving ? <Spinner size="tiny" /> : 'Submit'}
        </Button>
        <Button onClick={startEdit} disabled={selectedRow === null || draft !== null || saving}>
          Edit
        </Button>
        {/* ADMIN ONLY — a Manager logs PTO; approving it is what turns it into pay.
            The API enforces it; this keeps the affordance honest. */}
        {isAdmin ? (
          <Button
            appearance="primary"
            onClick={() => void approveSelected()}
            disabled={
              selectedRow === null ||
              draft !== null ||
              saving ||
              approving ||
              selectedRow.sentToNetsuiteAt !== null
            }
          >
            {approving ? <Spinner size="tiny" /> : 'Approve & post to NetSuite'}
          </Button>
        ) : null}
        <Button
          onClick={() => setConfirmDelete(true)}
          disabled={selectedRow === null || draft !== null || saving}
        >
          Delete
        </Button>
        {draft !== null ? (
          <Button appearance="subtle" onClick={cancelDraft} disabled={saving}>
            Cancel
          </Button>
        ) : null}

        {approveResult !== null ? (
          <MessageBar intent={approveResult.startsWith('Approved') ? 'success' : 'warning'}>
            <MessageBarBody>{approveResult}</MessageBarBody>
          </MessageBar>
        ) : null}

      {/* Employee filter. The Employee COLUMN is gone — the block header names
            the person — so filtering by person lives here instead. */}
        <Dropdown
          className={styles.employeeFilter}
          size="small"
          aria-label="Filter by employee"
          placeholder="All employees"
          value={filterLabel}
          selectedOptions={[employeeFilter]}
          onOptionSelect={(_, d) => setEmployeeFilter(d.optionValue ?? '')}
        >
          <Option value="" text="All employees">
            All employees
          </Option>
          {employeeChoices.map((choice) => (
            <Option key={choice.id} value={choice.id} text={choice.name}>
              {choice.name}
            </Option>
          ))}
        </Dropdown>

        <div className={styles.spacer} />
        <Text className={styles.hint}>
          {filtered.length} of {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
        </Text>
        {activeCount > 0 || employeeFilter !== '' ? (
          <Button
            appearance="subtle"
            size="small"
            onClick={() => {
              clearAll();
              setEmployeeFilter('');
            }}
          >
            Clear filters
          </Button>
        ) : null}
      </div>

      {actionError !== null ? (
        <div role="alert">
          <Text className={styles.error}>{actionError}</Text>
        </div>
      ) : null}

      <div className={styles.scroller}>
        <table className={styles.table} aria-label="PTO entries by employee">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.columnId} className={styles.headCell} scope="col">
                  {col.header}
                </th>
              ))}
              <th className={styles.headCell} scope="col">
                Flag
              </th>
            </tr>
            {/* Filter row: each column offers ONLY the values currently in it and
                matches them exactly — picking "IT" can no longer also match
                "Quality, Safety & Compliance" the way a substring search did. */}
            <tr className={styles.filterRow}>
              {columns.map((col) => {
                const selected = selections[col.columnId] ?? [];
                const available = filterOptions[col.columnId] ?? [];
                return (
                  <td key={col.columnId} className={styles.filterCell}>
                    <Dropdown
                      className={styles.filterDropdown}
                      size="small"
                      multiselect
                      placeholder="All"
                      aria-label={`Filter by ${col.header}`}
                      value={selected.length === 0 ? '' : selected.join(', ')}
                      selectedOptions={[...selected]}
                      onOptionSelect={(_, d) => setSelection(col.columnId, d.selectedOptions)}
                    >
                      {available.map((value) => (
                        <Option key={value} value={value} text={value}>
                          {value}
                        </Option>
                      ))}
                    </Dropdown>
                  </td>
                );
              })}
              <td className={styles.filterCell} />
            </tr>
          </thead>

          {/* A new entry gets its own block at the top, and the employee picker
              sits in that block's header — the same slot a saved block uses for
              the name, so the grouping metaphor holds while creating. */}
          {draft !== null && draft.mode === 'new' ? (
            <tbody>
              <tr>
                <td className={styles.glanceCell} colSpan={TOTAL_COLUMNS}>
                  <div className={styles.newBlockLabel}>
                    <span className={styles.glanceName}>New PTO entry for</span>
                    <Dropdown
                      size="small"
                      aria-label="Employee"
                      placeholder="Select employee"
                      value={
                        options.employees.find((e) => e.id === draft.form.employeeId)?.name ?? ''
                      }
                      selectedOptions={draft.form.employeeId === '' ? [] : [draft.form.employeeId]}
                      onOptionSelect={(_, d) => {
                        const picked = options.employees.find((e) => e.id === d.optionValue);
                        // Pre-fill the department from the employee's assignment;
                        // still editable.
                        patchForm({
                          employeeId: d.optionValue ?? '',
                          ...(picked?.departmentId != null && draft.form.departmentId === ''
                            ? { departmentId: picked.departmentId }
                            : {}),
                        });
                      }}
                    >
                      {options.employees.map((e) => (
                        <Option key={e.id} value={e.id} text={e.name}>
                          {e.name}
                        </Option>
                      ))}
                    </Dropdown>
                  </div>
                </td>
              </tr>
              <tr className={styles.draftRow}>
                <DraftCells
                  form={draft.form}
                  options={options}
                  styles={styles}
                  patchForm={patchForm}
                  draftFlag={draftFlag}
                  statusLabel="New"
                />
              </tr>
            </tbody>
          ) : null}

          {blocks.map((block) => (
            <tbody key={block.employeeId}>
              <tr>
                <td className={styles.glanceCell} colSpan={TOTAL_COLUMNS}>
                  <div className={styles.glanceHeader}>
                    <span className={styles.glanceName}>{block.employeeName}</span>
                    <PtoBalances standing={standings.get(block.employeeId) ?? null} styles={styles} />
                    <div className={styles.glanceTotalsRow}>
                      {block.totals.upcoming.count > 0 ? (
                        <span className={styles.upcoming}>
                          {bucketText(block.totals.upcoming)} upcoming
                        </span>
                      ) : null}
                      {block.totals.active.count > 0 ? (
                        <span className={styles.active}>
                          {bucketText(block.totals.active)} active
                        </span>
                      ) : null}
                      {block.totals.complete.count > 0 ? (
                        <span className={styles.complete}>
                          {bucketText(block.totals.complete)} complete
                        </span>
                      ) : null}
                      <span className={styles.glanceTotalGroup}>
                        <span className={styles.glanceTotalLabel}>Total</span>
                        <span>
                          {block.totals.total.count}{' '}
                          {block.totals.total.count === 1 ? 'entry' : 'entries'}
                        </span>
                        <span className={styles.glanceSep}>·</span>
                        <span>{block.totals.total.hours}h</span>
                      </span>
                    </div>
                  </div>
                </td>
              </tr>

              {block.entries.map((row) => {
                const editingThis = draft?.mode === 'edit' && draft.id === row.id;
                if (editingThis && draft !== null) {
                  return (
                    <tr key={row.id} className={styles.draftRow}>
                      <DraftCells
                        form={draft.form}
                        options={options}
                        styles={styles}
                        patchForm={patchForm}
                        draftFlag={draftFlag}
                        statusLabel={ptoStatusLabel(row.status)}
                      />
                    </tr>
                  );
                }
                return (
                  <tr
                    key={row.id}
                    className={mergeClasses(
                      styles.dataRow,
                      row.id === selectedId ? styles.selectedRow : undefined
                    )}
                    onClick={() => {
                      if (draft === null) {
                        setSelectedId(row.id === selectedId ? null : row.id);
                      }
                    }}
                    aria-selected={row.id === selectedId}
                  >
                    <td className={styles.cell}>{row.departmentName}</td>
                    <td className={mergeClasses(styles.cell, styles.cellNoWrap)}>
                      {row.startDate}
                    </td>
                    <td className={mergeClasses(styles.cell, styles.cellNoWrap)}>{row.endDate}</td>
                    <td className={mergeClasses(styles.cell, styles.hoursCell)}>
                      {row.reconciliation.reducedHours > 0 ? (
                        <>
                          <span className={styles.hoursReduced}>
                            {row.reconciliation.effectiveHours}
                          </span>
                          <span className={styles.hoursOriginal}>{row.hours}</span>
                        </>
                      ) : (
                        row.hours
                      )}
                    </td>
                    <td className={mergeClasses(styles.cell, styles.cellNoWrap)}>
                      <StatusText status={row.status} styles={styles} />
                    </td>
                    <td className={mergeClasses(styles.cell, styles.flagCell)}>
                      {/* The PTO-balance flag rides in the EXISTING Flag column rather
                          than adding one: this cell is already where a supervisor looks
                          for "something is wrong with this row". The reconciliation note
                          keeps its place beneath it. */}
                      {(() => {
                        const flag = flagFor(row);
                        if (flag === null || flag.verdict === 'ok') {
                          return null;
                        }
                        return (
                          <Tooltip content={flag.message} relationship="label">
                            <Badge
                              appearance="filled"
                              color={
                                flag.verdict === 'over'
                                  ? 'danger'
                                  : flag.verdict === 'low'
                                    ? 'warning'
                                    : 'informative'
                              }
                            >
                              {flag.verdict === 'over'
                                ? 'Over PTO allowance'
                                : flag.verdict === 'low'
                                  ? 'PTO nearly used up'
                                  : 'PTO balance unknown'}
                            </Badge>
                          </Tooltip>
                        );
                      })()}{' '}
                      {reconciliationNote(row)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          ))}
        </table>

        {blocks.length === 0 && draft === null ? (
          <div className={styles.empty}>
            <Text>
              {entries.length === 0
                ? 'No PTO entries to show. Choose "New PTO" to add one.'
                : 'No entries match the current filters.'}
            </Text>
          </div>
        ) : null}
      </div>

      <Text className={styles.hint}>
        A work day is {MAX_PTO_HOURS_PER_DAY} hours, Monday to Friday. A booking across several
        days must total exactly {MAX_PTO_HOURS_PER_DAY} × the work days in its range; book any
        partial day as its own single-day entry (1–{MAX_PTO_HOURS_PER_DAY} hours) so the hours
        land on the right date. New bookings must start after today ({today}). Completed
        entries are hidden — choose “Complete” in the Status filter to see them.
        {isAdmin
          ? ' As an administrator you see every employee’s PTO.'
          : ' You see PTO for the employees you supervise in NetSuite, plus any entry you created.'}
      </Text>

      <Dialog open={confirmDelete} onOpenChange={(_, d) => setConfirmDelete(d.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete this PTO entry?</DialogTitle>
            <DialogContent>
              {selectedRow !== null ? (
                <Text>
                  {selectedRow.employeeName} — {selectedRow.startDate} to {selectedRow.endDate} (
                  {selectedRow.hours} hours). This cannot be undone.
                </Text>
              ) : null}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setConfirmDelete(false)}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={() => void doDelete()} disabled={saving}>
                Delete
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}

/**
 * What to say about a booking that the clock has changed, or that cannot be
 * measured at all. Empty when the booking stands as entered.
 *
 * Worked hours take precedence over PTO, so a day never exceeds
 * MAX_PTO_HOURS_PER_DAY in total — the supervisor needs to see WHY the payable
 * figure differs from what was asked for, and on which dates.
 */
function reconciliationNote(row: PtoEntry): string {
  const { reconciliation } = row;
  if (reconciliation.unallocatable) {
    return row.startDate === row.endDate
      ? ''
      : 'This entry does not total 10 hours per work day, so its hours cannot be matched to ' +
          'particular days and will not be reconciled against clocked time. Re-enter it as ' +
          'full days plus a single-day entry for each partial day.';
  }
  if (reconciliation.reducedHours === 0) {
    return '';
  }
  const worked = reconciliation.days.filter((d) => d.workedHours > 0);
  const detail = worked
    .map((d) => `${d.date}: ${String(d.workedHours)}h worked → ${String(d.effectiveHours)}h PTO`)
    .join('; ');
  const blocked = reconciliation.blockedDates.length;
  return (
    `Reduced by ${String(reconciliation.reducedHours)}h because time was clocked on ` +
    `${String(worked.length)} of these days — ${detail}.` +
    (blocked > 0
      ? ` ${String(blocked)} day${blocked === 1 ? '' : 's'} had a full shift, so no PTO is payable ` +
        `${blocked === 1 ? 'that day' : 'those days'}.`
      : '')
  );
}

/** Status as coloured text, matching the block header's colour language. */
function StatusText({ status, styles }: { status: PtoStatus; styles: Styles }): JSX.Element {
  const cls =
    status === 'pending' ? styles.upcoming : status === 'active' ? styles.active : styles.complete;
  return <span className={cls}>{ptoStatusLabel(status)}</span>;
}

/** Props for the inline create/edit cells. */
interface DraftCellsProps {
  form: PtoEntryInput;
  options: PtoOptions;
  styles: Styles;
  patchForm: (patch: Partial<PtoEntryInput>) => void;
  draftFlag: string;
  statusLabel: string;
}

/**
 * The editable cells shared by New and Edit.
 *
 * The employee is NOT among them: on a new entry it is picked in the block header
 * above, and on an edit it is fixed for good — re-pointing a saved booking at a
 * different person rewrites payroll history silently, so that is a
 * delete-and-recreate.
 */
function DraftCells({
  form,
  options,
  styles,
  patchForm,
  draftFlag,
  statusLabel,
}: DraftCellsProps): JSX.Element {
  const departmentName = options.departments.find((d) => d.id === form.departmentId)?.name ?? '';

  return (
    <>
      <td className={styles.cell}>
        <Dropdown
          size="small"
          aria-label="Department"
          placeholder="Select department"
          value={departmentName}
          selectedOptions={form.departmentId === '' ? [] : [form.departmentId]}
          onOptionSelect={(_, d) => patchForm({ departmentId: d.optionValue ?? '' })}
        >
          {options.departments.map((dep) => (
            <Option key={dep.id} value={dep.id} text={dep.name}>
              {dep.name}
            </Option>
          ))}
        </Dropdown>
      </td>
      <td className={mergeClasses(styles.cell, styles.cellNoWrap)}>
        <input
          type="date"
          className={styles.dateInput}
          aria-label="Start date"
          value={form.startDate}
          onChange={(e) => patchForm({ startDate: e.target.value })}
        />
      </td>
      <td className={mergeClasses(styles.cell, styles.cellNoWrap)}>
        <input
          type="date"
          className={styles.dateInput}
          aria-label="End date"
          value={form.endDate}
          onChange={(e) => patchForm({ endDate: e.target.value })}
        />
      </td>
      <td className={mergeClasses(styles.cell, styles.hoursCell)}>
        <Input
          size="small"
          className={styles.numeric}
          type="number"
          step={1}
          min={1}
          aria-label="Hours"
          value={form.hours === 0 ? '' : String(form.hours)}
          onChange={(_, d) => {
            // Whole numbers only — a fractional entry is kept out at the source.
            const parsed = Number.parseInt(d.value, 10);
            patchForm({ hours: Number.isNaN(parsed) ? 0 : parsed });
          }}
        />
      </td>
      <td className={mergeClasses(styles.cell, styles.cellNoWrap)}>{statusLabel}</td>
      <td className={mergeClasses(styles.cell, styles.flagCell)}>
        {draftFlag === '' ? null : <Text className={styles.error}>{draftFlag}</Text>}
      </td>
    </>
  );
}

/**
 * One employee's PTO balances, per department, in the block header.
 *
 * ALWAYS PRESENT (Michael, 2026-09-13) — previously this appeared in a banner
 * only while a row was selected, so the figure a supervisor needs to approve was
 * hidden until after they had clicked.
 *
 * 🔥 ONE CHIP PER BUCKET, NEVER A TOTAL. An employee holds one PTO accrual per
 * department; the test employee holds nine at 40 hours each. The old banner
 * summed them and announced 360 hours to someone who has 40, then told the
 * supervisor a 20-hour IT booking left 340. These hours are not interchangeable
 * and not spendable together, so they are never added up here.
 */
function PtoBalances({
  standing,
  styles,
}: {
  standing: PtoStanding | null;
  styles: Record<string, string>;
}): JSX.Element | null {
  if (standing === null) {
    return null;
  }
  if (standing.unavailable !== undefined) {
    return (
      <div className={styles.balanceRow}>
        <span className={styles.balanceNote}>
          {`PTO balance unavailable — ${standing.unavailable}.`}
        </span>
      </div>
    );
  }
  // `buckets` is defensive against an API that predates it: the web app deploys
  // separately from the API, so for a few minutes one can be newer than the other
  // and `undefined.length` would blank the whole report.
  const buckets = standing.buckets ?? [];
  if (buckets.length === 0) {
    return (
      <div className={styles.balanceRow}>
        <span className={styles.balanceNote}>No PTO accrual on this employee in NetSuite.</span>
      </div>
    );
  }
  return (
    <div className={styles.balanceRow}>
      {buckets.map((b) => {
        const left = b.availableIfFutureAppliedHours;
        const over = left !== null && left < 0;
        return (
          <span key={b.nsPayrollItemId} className={styles.balanceChip}>
            <span className={styles.balanceDept}>{b.label}</span>
            <span className={over ? styles.balanceOver : styles.balanceLeft}>
              {left === null ? '—' : `${String(left)}h`}
            </span>
            {/* Only shown when there IS something booked, so the common row stays
                two words long rather than carrying a permanent "0h booked". */}
            {b.futureBookedHours > 0 && (
              <span className={styles.balanceBooked}>
                {`(${String(b.futureBookedHours)}h booked)`}
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
}
